import React from "react";

import {
  WebSettingsSections,
  AccountSections,
  TeamsSections,
  OrganizationSections,
  InternalSections,
  HoursAndPreferencesSections,
  SmartHoldsSections,
  IntegrationsSections,
  getExpandableTreeItemFromViewId,
  createSettingsViewId,
} from "../web-settings";

// util
// import { getCurrentOrg } from "@src/util/org.util";
// import { logger } from "@src/util/logger.util";
import { canAccessExperiments } from "@util/auth.util";

// material-ui
import {
  AccessTime,
  AddCircle,
  Assistant,
  BarChart,
  CalendarToday,
  CreditCard,
  DateRange,
  Flag,
  GroupWork,
  LockOutlined,
  Menu,
  NotInterested,
  Notifications,
  SettingsApplications,
  SwapHoriz,
  People,
} from "@material-ui/icons";
import { Box, IconButton, Typography } from "@material-ui/core";
import { TreeView, TreeItem } from "@material-ui/lab";
import { makeStyles } from "@material-ui/styles";

import { greys } from "@clockwise/web-commons/src/styles/color.styles";

//////////////////
// Web Settings Tree Item Component
//////////////////
type WebSettingsTreeItemProps = {
  settingsViewId: string;
  label: React.ReactNode;
  onClick: (settingsViewId: string) => void;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  locked?: boolean;
};

const useWebSettingsTreeItemStyles = makeStyles({
  group: {
    marginLeft: 0,
  },
  label: {
    display: "flex",
    alignItems: "center",
  },
  verticalLine: {
    height: 24,
    borderLeft: `2px solid ${greys.borderGrey}`,
  },
  lockIcon: {
    color: "#DADADA",
    width: 15,
    marginLeft: 15,
  },
  disabled: {
    pointerEvents: "none",
  },
});

function WebSettingsTreeItem(props: WebSettingsTreeItemProps) {
  const classes = useWebSettingsTreeItemStyles();
  return (
    <TreeItem
      nodeId={props.settingsViewId}
      label={
        <>
          {props.label}
          {props.locked ? <LockOutlined className={classes.lockIcon} /> : ""}
        </>
      }
      onClick={() => props.onClick(props.settingsViewId)}
      icon={props.icon || <div className={classes.verticalLine} />}
      classes={{
        group: classes.group,
        label: classes.label,
        root: props.locked ? classes.disabled : undefined,
      }}
    >
      {props.children}
    </TreeItem>
  );
}

//////////////////
// Web Settings Drawer Component
//////////////////
type WebSettingsDrawerProps = {
  onSelect: (settingsViewId: string) => void;
  settingsViewId: string;
};

const useWebSettingsDrawerStyles = makeStyles({
  container: {
    width: 300,
    borderRight: `1px solid ${greys.borderGrey}`,
    overflow: "auto",
    flexShrink: 0,
    transition: "all 0.35s ease-in-out",
    "&.collapsed": {
      width: 48,
      overflow: "hidden",
    },
    "@media (max-width: 768px)": {
      position: "absolute",
    },
  },
  box: {
    padding: "40px 15px 0 30px",
    flexGrow: 1,
    overflowY: "auto",
    "&.hidden": {
      display: "none",
    },
  },
  header: {
    fontWeight: "bold",
    padding: "25px 0",
  },
});

export function WebSettingsDrawer(props: WebSettingsDrawerProps) {
  const classes = useWebSettingsDrawerStyles();

  const isMobile = window.matchMedia("(max-width: 768px)").matches;

  // ~-~-~-~-~-~-~-
  // State
  // ~-~-~-~-~-~-~-
  const [drawerExpanded, setDrawerExpanded] = React.useState(!isMobile);
  const [expandedTreeItem, setExpandedTreeItem] = React.useState<string>(
    getExpandableTreeItemFromViewId(props.settingsViewId),
  );

  React.useEffect(() => {
    const maybeParentTreeItem = getExpandableTreeItemFromViewId(props.settingsViewId);
    setExpandedTreeItem(maybeParentTreeItem);
  }, [props.settingsViewId]);

  // ~-~-~-~-~-~-~-
  // Click Handlers
  // ~-~-~-~-~-~-~-s
  const onClickTreeItem = (settingsViewId: string) => {
    props.onSelect(settingsViewId);
  };

  const onClickToggleDrawerExpansion = () => {
    setDrawerExpanded(!drawerExpanded);
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  return (
    <div className={`${!drawerExpanded ? "collapsed" : ""} ${classes.container} cw-bg-white`}>
      <IconButton onClick={onClickToggleDrawerExpansion}>
        <Menu />
      </IconButton>
      <Box className={`${classes.box} ${!drawerExpanded ? "hidden" : ""}`}>
        <TreeView
          aria-label="controlled"
          expanded={[expandedTreeItem]}
          selected={[props.settingsViewId]}
        >
          <Typography variant="body1" className={classes.header}>
            Account
          </Typography>
          <WebSettingsTreeItem
            settingsViewId={createSettingsViewId(
              WebSettingsSections.Account,
              AccountSections.HoursAndPreferences,
            )}
            label="Hours and preferences"
            onClick={onClickTreeItem}
            icon={<AccessTime />}
          >
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Account,
                AccountSections.HoursAndPreferences,
                HoursAndPreferencesSections.WorkingHours,
              )}
              label="Working hours"
              onClick={onClickTreeItem}
            />
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Account,
                AccountSections.HoursAndPreferences,
                HoursAndPreferencesSections.MeetingHours,
              )}
              label="Meeting hours"
              onClick={onClickTreeItem}
            />
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Account,
                AccountSections.HoursAndPreferences,
                HoursAndPreferencesSections.TimeZone,
              )}
              label="Time zone"
              onClick={onClickTreeItem}
            />
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Account,
                AccountSections.HoursAndPreferences,
                HoursAndPreferencesSections.MeetingBreak,
              )}
              label="Meeting break"
              onClick={onClickTreeItem}
            />
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Account,
                AccountSections.HoursAndPreferences,
                HoursAndPreferencesSections.TimeOfDayPreference,
              )}
              label="Time of day preference"
              onClick={onClickTreeItem}
            />
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Account,
                AccountSections.HoursAndPreferences,
                HoursAndPreferencesSections.PreferredMeetingRooms,
              )}
              label="Preferred meeting rooms"
              onClick={onClickTreeItem}
            />
          </WebSettingsTreeItem>
          <WebSettingsTreeItem
            settingsViewId={createSettingsViewId(
              WebSettingsSections.Account,
              AccountSections.SmartHolds,
            )}
            label="Smart Holds"
            onClick={onClickTreeItem}
            icon={<Assistant />}
          >
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Account,
                AccountSections.SmartHolds,
                SmartHoldsSections.FocusTime,
              )}
              label="Focus Time"
              onClick={onClickTreeItem}
            />
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Account,
                AccountSections.SmartHolds,
                SmartHoldsSections.LunchHold,
              )}
              label="Lunch hold"
              onClick={onClickTreeItem}
            />
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Account,
                AccountSections.SmartHolds,
                SmartHoldsSections.TravelTime,
              )}
              label="Travel time"
              onClick={onClickTreeItem}
            />
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Account,
                AccountSections.SmartHolds,
                SmartHoldsSections.PersonalCalendarHold,
              )}
              label="Personal calendar hold"
              onClick={onClickTreeItem}
            />
          </WebSettingsTreeItem>
          <WebSettingsTreeItem
            settingsViewId={createSettingsViewId(
              WebSettingsSections.Account,
              AccountSections.Integrations,
            )}
            label="Integrations"
            onClick={onClickTreeItem}
            icon={<SwapHoriz />}
          >
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Account,
                AccountSections.Integrations,
                IntegrationsSections.Slack,
              )}
              label="Slack"
              onClick={onClickTreeItem}
            />
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Account,
                AccountSections.Integrations,
                IntegrationsSections.Zoom,
              )}
              label="Zoom"
              onClick={onClickTreeItem}
            />
            <WebSettingsTreeItem
              settingsViewId={createSettingsViewId(
                WebSettingsSections.Account,
                AccountSections.Integrations,
                IntegrationsSections.Asana,
              )}
              label="Asana"
              onClick={onClickTreeItem}
            />
          </WebSettingsTreeItem>
          <WebSettingsTreeItem
            settingsViewId={createSettingsViewId(
              WebSettingsSections.Account,
              AccountSections.EventColorCoding,
            )}
            label="Event color coding"
            onClick={onClickTreeItem}
            icon={<DateRange />}
          />
          <WebSettingsTreeItem
            settingsViewId={createSettingsViewId(
              WebSettingsSections.Account,
              AccountSections.TeamAvailabilityCalendar,
            )}
            label="Team availability calendar"
            onClick={onClickTreeItem}
            icon={<CalendarToday />}
          />
          <WebSettingsTreeItem
            settingsViewId={createSettingsViewId(
              WebSettingsSections.Account,
              AccountSections.TeamNoMeetingDay,
            )}
            label="Team no meeting day"
            onClick={onClickTreeItem}
            icon={<NotInterested />}
          />
          <WebSettingsTreeItem
            settingsViewId={createSettingsViewId(
              WebSettingsSections.Account,
              AccountSections.EmailsAndNotifications,
            )}
            label="Emails and notifications"
            onClick={onClickTreeItem}
            icon={<Notifications />}
          />
          <Typography variant="body1" className={classes.header}>
            Teams
          </Typography>
          <WebSettingsTreeItem
            settingsViewId={createSettingsViewId(
              WebSettingsSections.Teams,
              TeamsSections.CreateOrJoinTeam,
            )}
            label="Create or join team"
            onClick={onClickTreeItem}
            icon={<AddCircle />}
          />
          <Typography variant="body1" className={classes.header}>
            Organization
          </Typography>
          <WebSettingsTreeItem
            settingsViewId={createSettingsViewId(
              WebSettingsSections.Organization,
              OrganizationSections.Analytics,
            )}
            label="Analytics"
            onClick={onClickTreeItem}
            icon={<BarChart />}
          />
          <WebSettingsTreeItem
            settingsViewId={createSettingsViewId(
              WebSettingsSections.Organization,
              OrganizationSections.Members,
            )}
            label="Members"
            onClick={onClickTreeItem}
            icon={<People />}
          />
          <WebSettingsTreeItem
            settingsViewId={createSettingsViewId(
              WebSettingsSections.Organization,
              OrganizationSections.Teams,
            )}
            label="Teams"
            onClick={onClickTreeItem}
            icon={<GroupWork />}
            locked
          />
          <WebSettingsTreeItem
            settingsViewId={createSettingsViewId(
              WebSettingsSections.Organization,
              OrganizationSections.PlansAndBilling,
            )}
            label="Plans and billing"
            onClick={onClickTreeItem}
            icon={<CreditCard />}
          />
          <WebSettingsTreeItem
            settingsViewId={createSettingsViewId(
              WebSettingsSections.Organization,
              OrganizationSections.OrganizationSettings,
            )}
            label="Organization settings"
            onClick={onClickTreeItem}
            icon={<SettingsApplications />}
          />
          {canAccessExperiments() ? (
            <>
              <Typography variant="body1" className={classes.header}>
                Internal
              </Typography>
              <WebSettingsTreeItem
                settingsViewId={createSettingsViewId(
                  WebSettingsSections.Internal,
                  InternalSections.Experiments,
                )}
                label="Experiments"
                onClick={onClickTreeItem}
                icon={<Flag />}
              />
            </>
          ) : (
            ""
          )}
        </TreeView>
      </Box>
    </div>
  );
}
